<template>
  <MainHeader />
  <MainSidebar />
  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu
      pageTitle="edit_sale"
      :items="breadcrumbItems"
      :showHome="false"
    />
    <div class="row mb-40">
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.date") }}</label>
          <input
            type="date"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
            v-model="saleDate"
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.reference_no") }} <span class="text-danger">*</span></label>
          <input
            type="text"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
            :placeholder="$t('sales_page.reference_no')"
            v-model="referenceNo"
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.customer") }} <span class="text-danger">*</span></label>
          <select
            class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55"
            v-model="selectedCustomerId"
          >
            <option :value="null">{{ customers.length ? $t("sales_page.select_customer") : $t("sales_page.loading_customers") }}</option>
            <option v-for="customer in customers" :key="customer.id" :value="customer.id">
              {{ customer.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.warehouse") }} <span class="text-danger">*</span></label>
          <select
            class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55"
            v-model="selectedWarehouseId"
          >
            <option :value="null">{{ $t("sales_page.select_warehouse") }}</option>
            <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
              {{ warehouse.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-12">
        <div class="form-group">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.choose_product") }}</label>
          <div class="search-area style-two position-relative w-100">
            <div class="position-relative">
              <input
                type="text"
                :placeholder="$t('sales_page.scan_search_product')"
                class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
                v-model="searchQuery"
                @keyup.enter="onSearch"
                @input="onSearchInput"
              />
              <button
                type="button"
                class="bg-transparent border-0 position-absolute top-0 end-0 h-100 pt-0 py-0 px-2"
                @click="onSearch"
              >
                <img src="../../assets/img/icons/search.svg" alt="Image" />
              </button>

              <div
                v-if="searchResults.length > 0"
                class="position-absolute w-100 bg-white shadow rounded-1 mt-1 z-3 overflow-auto"
                style="max-height: 300px"
              >
                <ul class="list-unstyled mb-0">
                  <li
                    v-for="product in searchResults"
                    :key="product.id"
                    class="p-2 border-bottom product-search-item"
                    :class="{ 'cursor-pointer hover-bg-light': canSelectProduct(product), 'disabled-product': !canSelectProduct(product) }"
                    @click="selectProduct(product)"
                  >
                    <div class="d-flex justify-content-between">
                      <span class="fw-bold">{{ product.name }}</span>
                      <span class="text-muted">{{ product.code }}</span>
                    </div>
                    <div class="d-flex justify-content-between fs-12 text-muted">
                      <span>{{ $t("sales_page.price") }}: ${{ product.price }}</span>
                      <span v-if="Number(product.type) === 1">
                        {{ getAvailableStock(product) > 0 ? `${$t("sales_page.stock")}: ${getAvailableStock(product)}` : $t("sales_page.out_of_stock") }}
                      </span>
                      <span v-else>{{ $t("sales_page.service_item") }}</span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <SelectedProducts />
    <SubmitPurchase />

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";

import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import SelectedProducts from "../../components/Sales/CreateSales/SelectedProducts.vue";
import SubmitPurchase from "../../components/Sales/CreateSales/SubmitPurchase.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";

import customerService, { type Customer } from "../../services/customer";
import warehouseService, { type Warehouse } from "../../services/warehouse";
import productService from "../../services/product";
import salesService from "../../services/sales";
import stateStore from "../../utils/store";

export default defineComponent({
  name: "EditSalesPage",
  components: {
    MainHeader,
    MainSidebar,
    BreadcrumbMenu,
    SelectedProducts,
    SubmitPurchase,
    MainFooter,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();

    const customers = ref<Customer[]>([]);
    const warehouses = ref<Warehouse[]>([]);
    const searchQuery = ref("");
    const searchResults = ref<any[]>([]);
    const saleDate = ref(new Date().toISOString().split("T")[0]);
    const breadcrumbItems = [
      { label: "sales_list", to: "/sales-list" },
      { label: "edit_sale" },
    ];

    const selectedCustomerId = computed({
      get: () => stateStore.selectedCustomerId,
      set: (val: number | null) => {
        stateStore.selectedCustomerId = val ? Number(val) : null;
      },
    });

    const selectedWarehouseId = computed({
      get: () => stateStore.selectedWarehouseId,
      set: (val: number | null) => {
        stateStore.selectedWarehouseId = val ? Number(val) : null;
      },
    });

    const referenceNo = computed({
      get: () => stateStore.referenceNo,
      set: (val: string) => {
        stateStore.referenceNo = val;
      },
    });

    const getWarehouseStock = (product: any, warehouseId: number | null) => {
      const stocks = Array.isArray(product.stocks) ? product.stocks : [];
      if (!stocks.length) {
        return 0;
      }

      if (!warehouseId) {
        return stocks.reduce(
          (sum: number, stock: any) => sum + Number(stock.qty ?? stock.quantity ?? 0),
          0
        );
      }

      return stocks
        .filter((stock: any) => Number(stock.warehouse_id) === Number(warehouseId))
        .reduce((sum: number, stock: any) => sum + Number(stock.qty ?? stock.quantity ?? 0), 0);
    };

    const getAvailableStock = (product: any) => {
      if (Number(product.type) !== 1) {
        return Number(product.qty ?? 0);
      }

      return getWarehouseStock(product, selectedWarehouseId.value);
    };

    const canSelectProduct = (product: any) => {
      if (Number(product.type) !== 1) return true;
      return getAvailableStock(product) > 0;
    };

    const fetchData = async () => {
      const [customerRes, warehouseRes] = await Promise.all([
        customerService.getCustomers(),
        warehouseService.getWarehouses(),
      ]);

      customers.value = customerRes;
      warehouses.value = warehouseRes;
    };

    const populateSale = async () => {
      const saleId = Number(route.params.id);
      if (!saleId) {
        await Swal.fire({
          icon: "error",
          title: t("sales_page.invalid_sale"),
          text: t("sales_page.invalid_sale_text"),
        });
        router.push("/sales-list");
        return;
      }

      const sale = await salesService.getSaleDetails(saleId);
      saleDate.value = (sale.date || sale.created_at || "").split("T")[0] || saleDate.value;
      stateStore.referenceNo = sale.reference_no || "";
      stateStore.selectedCustomerId = sale.customer_id ? Number(sale.customer_id) : null;
      stateStore.selectedWarehouseId = sale.warehouse_id ? Number(sale.warehouse_id) : null;
      stateStore.clearSelectedProducts();
      stateStore.setActivePromotion(null);
      stateStore.referenceNo = sale.reference_no || "";
      stateStore.selectedCustomerId = sale.customer_id ? Number(sale.customer_id) : null;
      stateStore.selectedWarehouseId = sale.warehouse_id ? Number(sale.warehouse_id) : null;
      stateStore.setOrderDiscount(Number(sale.discount || 0), "fixed");
      stateStore.setOrderTax(Number(sale.tax || 0), "fixed");
      stateStore.setShippingCost(Number(sale.shipping || 0));

      const products = await Promise.all(
        (sale.items || []).map(async (item) => {
          try {
            const product = await productService.getProduct(item.product_id);
            const warehouseStock = getWarehouseStock(product, stateStore.selectedWarehouseId);
            return {
              id: item.product_id,
              name: item.product_name,
              code: item.product_code,
              qty: Number(item.quantity) || 1,
              stock: warehouseStock + Number(item.quantity || 0),
              price: Number(item.unit_price ?? item.price ?? 0),
              discount: 0,
              discount_type: "fixed" as const,
              size_id: null,
              sugar_id: null,
              product_sizes: product.product_sizes || [],
              type: product.type,
            };
          } catch {
            return {
              id: item.product_id,
              name: item.product_name,
              code: item.product_code,
              qty: Number(item.quantity) || 1,
              stock: Number(item.quantity) || 1,
              price: Number(item.unit_price ?? item.price ?? 0),
              discount: 0,
              discount_type: "fixed" as const,
              size_id: null,
              sugar_id: null,
              product_sizes: [],
              type: 1,
            };
          }
        })
      );

      stateStore.selectedProducts = products;
    };

    const searchProducts = async () => {
      if (!searchQuery.value.length) {
        searchResults.value = [];
        return;
      }

      const isSpaceOnly = searchQuery.value.length > 0 && searchQuery.value.trim().length === 0;
      const searchValue = isSpaceOnly ? undefined : searchQuery.value;
      const products = await productService.getProducts({
        search: searchValue,
        warehouse_id: selectedWarehouseId.value ?? undefined,
      });
      searchResults.value = products;
    };

    const onSearch = async () => {
      await searchProducts();
      if (searchResults.value.length === 1 && searchQuery.value.trim().length > 0 && canSelectProduct(searchResults.value[0])) {
        selectProduct(searchResults.value[0]);
      }
    };

    const onSearchInput = async () => {
      if (!searchQuery.value.length) {
        searchResults.value = [];
        return;
      }
      await searchProducts();
    };

    const selectProduct = (product: any) => {
      if (!canSelectProduct(product)) return;
      stateStore.addSelectedProduct(product);
      searchQuery.value = "";
      searchResults.value = [];
    };

    onMounted(async () => {
      await fetchData();
      await populateSale();
    });

    return {
      customers,
      warehouses,
      breadcrumbItems,
      searchQuery,
      searchResults,
      saleDate,
      selectedCustomerId,
      selectedWarehouseId,
      referenceNo,
      getAvailableStock,
      canSelectProduct,
      onSearch,
      onSearchInput,
      selectProduct,
    };
  },
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.hover-bg-light:hover {
  background-color: #f8f9fa;
}

.z-3 {
  z-index: 1050;
}

.disabled-product {
  opacity: 0.55;
  cursor: not-allowed;
  background-color: #f8f9fa;
}
</style>
